'use client'

import { useEffect, useState } from 'react'

const CHALLENGE_SEQUENCE = '🐭🐭🐭🐭🐭ERR'
const CHALLENGE_CLICK_COUNT = 5
const CHALLENGE_TIME_LIMIT = 5_000

/**
 * Returns formatted error details when the user performs a specific sequence:
 * - 5 clicks while holding the Alt key
 * - Press keys E, R and R
 * - Everything must be done within 5 seconds
 */
export function RevealError({ error }: { error: Error }) {
  const [displayErrorDetails, setDisplayErrorDetails] = useState(false)

  useEffect(() => {
    let clickCount = 0
    let clickTimeout: NodeJS.Timeout | null = null
    let keySequence: string = ''
    let isAltPressed = false

    function resetClickDetection() {
      clickCount = 0

      if (clickTimeout) {
        clearTimeout(clickTimeout)
        clickTimeout = null
      }

      keySequence = ''
    }

    function handleClick() {
      if (!isAltPressed) {
        return
      }

      clickCount += 1
      keySequence += '🐭'

      if (clickCount === CHALLENGE_CLICK_COUNT) {
        // wait for next keys
        if (clickTimeout) {
          clearTimeout(clickTimeout)
        }

        clickTimeout = setTimeout(() => {
          resetClickDetection()
        }, CHALLENGE_TIME_LIMIT) //
      } else if (clickCount > CHALLENGE_CLICK_COUNT) {
        resetClickDetection()
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Alt') {
        isAltPressed = true

        return
      }

      if (clickCount === CHALLENGE_CLICK_COUNT) {
        keySequence += e.key.toUpperCase()

        if (keySequence.length > CHALLENGE_SEQUENCE.length) {
          resetClickDetection()

          return
        }

        if (keySequence === CHALLENGE_SEQUENCE) {
          setDisplayErrorDetails(true)
          resetClickDetection()
        }
      }
    }

    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === 'Alt') {
        isAltPressed = false
      }
    }

    const controller = new AbortController()
    const { signal } = controller

    window.addEventListener('pointerup', handleClick, { signal })
    window.addEventListener('keydown', handleKeyDown, { signal })
    window.addEventListener('keyup', handleKeyUp, { signal })

    return () => {
      controller.abort()

      if (clickTimeout) {
        clearTimeout(clickTimeout)
      }
    }
  }, [])

  if (!displayErrorDetails) {
    return null
  }

  return (
    <pre className="w-full overflow-auto pb-4 text-left text-sm text-brand-2-300">
      {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
      {'\n'.repeat(2)}
      {(error as Error).stack}
    </pre>
  )
}
